import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Space,
  Popconfirm,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';

const JugadoresClub = () => {
  const [jugadores, setJugadores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingJugador, setEditingJugador] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const loadCategorias = async () => {
    try {
      const response = await apiClient.get('/categorias');
      if (response.ok) {
        const data = await response.json();
        setCategorias(data.filter((c) => c.cat_activo));
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const loadJugadores = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/club/jugadores');
      if (response.ok) {
        const data = await response.json();
        setJugadores(data);
      } else {
        message.error('Error al cargar los jugadores');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los jugadores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategorias();
    loadJugadores();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCreate = () => {
    setEditingJugador(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingJugador(record);
    form.setFieldsValue({
      nombre: record.jcl_nombre,
      apellido: record.jcl_apellido,
      telefono: record.jcl_telefono,
      email: record.jcl_email,
      sexo: record.jcl_sexo || undefined,
      nacimiento: record.jcl_nacimiento ? dayjs(record.jcl_nacimiento) : null,
      categoria: record.jcl_categoria || undefined,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        nombre: values.nombre,
        apellido: values.apellido,
        telefono: values.telefono || null,
        email: values.email || null,
        sexo: values.sexo || null,
        nacimiento: values.nacimiento ? values.nacimiento.format('YYYY-MM-DD') : null,
        categoria: values.categoria || null,
      };

      if (editingJugador) {
        const response = await apiClient.put(`/club/jugadores/${editingJugador.jcl_id}`, payload);
        if (response.ok) {
          message.success('Jugador actualizado correctamente');
          setModalVisible(false);
          loadJugadores();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el jugador');
        }
      } else {
        const response = await apiClient.post('/club/jugadores', payload);
        if (response.ok) {
          message.success('Jugador creado correctamente');
          setModalVisible(false);
          loadJugadores();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el jugador');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error('Error al guardar el jugador');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/club/jugadores/${id}`);
      if (response.ok) {
        message.success('Jugador eliminado correctamente');
        loadJugadores();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el jugador');
      }
    } catch (error) {
      message.error('Error al eliminar el jugador');
    }
  };

  const formatTelefonoWhatsApp = (telefono) => {
    if (!telefono) return '-';
    const soloNumeros = telefono.replace(/\D/g, '');
    if (soloNumeros.length < 10) return telefono;
    const url = `https://wa.me/${soloNumeros}`;
    return (
      <Space size="small">
        <span>{telefono}</span>
        <a href={url} target="_blank" rel="noopener noreferrer" title="Abrir WhatsApp">
          <MessageOutlined style={{ color: '#25D366' }} />
        </a>
      </Space>
    );
  };

  const columns = [
    {
      title: 'Apellido',
      dataIndex: 'jcl_apellido',
      key: 'jcl_apellido',
      sorter: (a, b) => (a.jcl_apellido || '').localeCompare(b.jcl_apellido || ''),
    },
    {
      title: 'Nombre',
      dataIndex: 'jcl_nombre',
      key: 'jcl_nombre',
      sorter: (a, b) => (a.jcl_nombre || '').localeCompare(b.jcl_nombre || ''),
    },
    {
      title: 'Teléfono (WhatsApp)',
      dataIndex: 'jcl_telefono',
      key: 'jcl_telefono',
      render: (tel) => formatTelefonoWhatsApp(tel),
    },
    {
      title: 'Email',
      dataIndex: 'jcl_email',
      key: 'jcl_email',
      ellipsis: true,
    },
    {
      title: 'Sexo',
      dataIndex: 'jcl_sexo',
      key: 'jcl_sexo',
      width: 100,
      render: (sexo) => {
        const map = { M: 'Masculino', F: 'Femenino', O: 'Otro' };
        return map[sexo] || '-';
      },
    },
    {
      title: 'Nacimiento',
      dataIndex: 'jcl_nacimiento',
      key: 'jcl_nacimiento',
      width: 120,
      render: (fecha) => (fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_nombre',
      key: 'jcl_categoria',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      fixed: isMobile ? undefined : 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este jugador?"
            onConfirm={() => handleDelete(record.jcl_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title="Jugadores del Club"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadJugadores} loading={loading}>
              Actualizar
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Nuevo Jugador
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={jugadores}
          loading={loading}
          rowKey="jcl_id"
          scroll={{ x: isMobile ? 800 : 'auto' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} jugadores`,
          }}
        />

        <Modal
          title={editingJugador ? 'Editar Jugador' : 'Nuevo Jugador'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => setModalVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 520}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: 'El nombre es requerido' }, { max: 100 }]}
            >
              <Input placeholder="Nombre" />
            </Form.Item>

            <Form.Item
              name="apellido"
              label="Apellido"
              rules={[{ required: true, message: 'El apellido es requerido' }, { max: 100 }]}
            >
              <Input placeholder="Apellido" />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono (WhatsApp)"
              help="Formato: código país + número sin espacios, ej: 5491112345678"
            >
              <Input placeholder="Ej: 5491112345678" maxLength={20} />
            </Form.Item>

            <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email no válido' }]}>
              <Input placeholder="email@ejemplo.com" />
            </Form.Item>

            <Form.Item name="sexo" label="Sexo">
              <Select placeholder="Seleccione sexo" allowClear>
                <Select.Option value="M">Masculino</Select.Option>
                <Select.Option value="F">Femenino</Select.Option>
                <Select.Option value="O">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="nacimiento" label="Fecha de nacimiento">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Seleccione fecha" />
            </Form.Item>

            <Form.Item name="categoria" label="Categoría">
              <Select placeholder="Seleccione categoría" allowClear showSearch optionFilterProp="children">
                {categorias.map((cat) => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
                    {cat.cat_descripcion}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </MainLayout>
  );
};

export default JugadoresClub;
